import type { ReactNode } from "react";
import { BrandMark } from "@cntrl/ui";
import { CgMark } from "./CgMark.js";
import styles from "./AuthCard.module.scss";

export interface AuthCardProps {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** Render inside a dialog that already carries the chrome (ART-307: the /apply page's log-in dialog): no
   *  full-screen ground, no brand mark, no title (the dialog's header names it), no attribution. */
  embedded?: boolean;
}

/**
 * The full-screen, VERTICALLY CENTRED auth card (it was top-anchored at ~18vh until 2026-10-09). The
 * keyboard-stability lesson — a centred card jumps when the iOS keyboard opens — is kept by centring in the space
 * ABOVE the keyboard (the screen's bottom padding grows by --keyboard-inset). BrandMark 40px on top
 * (crimson-on-light). Below the card: the parent-company attribution badge (auth-gate row →
 * ) — "a product of · CNTRL Global", mono/uppercase/low-opacity, with the single-source CNTRL Global mark.
 */
export function AuthCard({ title, subtitle, children, footer, embedded }: AuthCardProps) {
  if (embedded) {
    return (
      <div className={styles.embedded} aria-label={title} role="group">
        {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        {children}
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    );
  }
  return (
    <main className={styles.screen}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <BrandMark size={40} />
        </div>
        <h1 className={styles.title}>{title}</h1>
        {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        {children}
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
      <p className={styles.attribution}>
        <span>A product of</span>
        <CgMark className={styles.attributionMark} />
        <span>CNTRL&nbsp;Global</span>
      </p>
    </main>
  );
}
